import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Clock, Bell, Award, Camera, Users, Folder, Image } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

function Tile({ icon: Icon, label, value, accent, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 min-w-[72px] bg-white/10 hover:bg-white/20 rounded-xl p-2.5 text-left transition-colors"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent}`} />
        <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-wide leading-tight">{label}</span>
      </div>
      <p className="text-xl font-bold text-white leading-none">{value}</p>
    </button>
  );
}

export const WidgetCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/widget/summary`).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data) return null;
  const { role } = data;
  const isStaff = ['employee', 'supervisor', 'admin'].includes(role);
  const isAdmin = ['admin', 'supervisor'].includes(role);

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 shadow-lg p-4 mb-6"
      data-testid="widget-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center text-white font-black text-sm">D</div>
          <p className="text-sm font-bold text-white">At a Glance</p>
        </div>
        {isStaff && (
          <span
            className={`text-[10px] font-bold px-2 py-1 rounded-full ${data.clocked_in ? 'bg-green-500/20 text-green-400' : 'bg-slate-600/40 text-slate-300'}`}
            data-testid="widget-clock-status"
          >
            {data.clocked_in ? '● Clocked In' : 'Clocked Out'}
          </span>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {isStaff && (
          <Tile icon={Clock} label="Today" value={`${data.today_hours ?? 0}h`} accent="text-orange-400" onClick={() => navigate('/timeclock')} testId="widget-today-hours" />
        )}
        {isStaff && (
          <Tile icon={Clock} label="Week" value={`${data.week_hours ?? 0}h`} accent="text-sky-400" onClick={() => navigate('/timeclock')} testId="widget-week-hours" />
        )}
        {isStaff && (
          <Tile icon={Award} label="Badges" value={data.badges_new > 0 ? `${data.badges_total} +${data.badges_new}` : data.badges_total ?? 0} accent="text-yellow-400" onClick={() => navigate('/leaderboard')} testId="widget-badges" />
        )}
        {isAdmin && (
          <Tile icon={Users} label="On Clock" value={data.team_clocked_in ?? 0} accent="text-blue-400" onClick={() => navigate('/timeclock')} testId="widget-team-clocked-in" />
        )}
        {isAdmin && data.pending_photos > 0 && (
          <Tile icon={Camera} label="To Approve" value={data.pending_photos} accent="text-rose-400" onClick={() => navigate('/photos/pending')} testId="widget-pending-photos" />
        )}
        {role === 'client' && (
          <Tile icon={Folder} label="Projects" value={data.projects ?? 0} accent="text-blue-400" onClick={() => navigate('/dashboard')} testId="widget-client-projects" />
        )}
        {role === 'client' && (
          <Tile icon={Image} label="New Photos" value={data.new_photos ?? 0} accent="text-emerald-400" onClick={() => navigate('/dashboard')} testId="widget-client-new-photos" />
        )}
        {role === 'sub_contractor' && (
          <Tile icon={Image} label="New For You" value={data.new_items ?? 0} accent="text-emerald-400" onClick={() => navigate('/dashboard')} testId="widget-sub-new-items" />
        )}
        <Tile icon={Bell} label="Alerts" value={data.unread_alerts ?? 0} accent="text-purple-400" onClick={() => navigate('/notifications')} testId="widget-alerts" />
      </div>
    </div>
  );
};

export default WidgetCard;
